import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Video, Loader2, X, LogOut, Users, Menu, Image, Camera
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';

// Custom Wedding Rings Icon (two gold bands)
const WeddingRingsIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 32 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    {/* Left ring */}
    <ellipse cx="11" cy="12" rx="8" ry="9" stroke="#D4AF37" strokeWidth="2.5" fill="none"/>
    {/* Right ring */}
    <ellipse cx="21" cy="12" rx="8" ry="9" stroke="#D4AF37" strokeWidth="2.5" fill="none"/>
  </svg>
);

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ClientVideosView = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  
  const [gallery, setGallery] = useState(null);
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  useEffect(() => {
    fetchGalleryData();
  }, []);

  const fetchGalleryData = async () => {
    try {
      const res = await axios.get(`${API}/gallery`);
      setGallery({
        id: res.data.id,
        title: res.data.title,
        event_date: res.data.event_date
      });
      setVideos(res.data.videos || []);
    } catch (error) {
      console.error('Error fetching gallery:', error);
      if (error.response?.status === 401) {
        logout();
        navigate('/');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Sidebar */}
      {sidebarOpen && (
        <div className="fixed inset-0 z-50">
          {/* Overlay */}
          <div 
            className="absolute inset-0 bg-black/30"
            onClick={() => setSidebarOpen(false)}
          />
          {/* Sidebar panel */}
          <div className="absolute left-0 top-0 h-full w-64 bg-white shadow-xl">
            <div className="p-6">
              <div className="flex justify-between items-center mb-8">
                <span className="font-medium text-gray-800">Menu</span>
                <button 
                  onClick={() => setSidebarOpen(false)}
                  className="p-2 hover:bg-gray-100 rounded-lg"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              
              <nav className="space-y-2">
                <button
                  onClick={() => { navigate('/gallery'); setSidebarOpen(false); }}
                  className="w-full flex items-center gap-3 px-4 py-3 text-left text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <Image className="w-5 h-5" />
                  Tutte le foto
                </button>
                <button
                  onClick={() => setSidebarOpen(false)}
                  className="w-full flex items-center gap-3 px-4 py-3 text-left text-gray-700 bg-gray-100 rounded-lg transition-colors"
                >
                  <Video className="w-5 h-5" />
                  Tutti i video
                </button>
                <button
                  onClick={() => { navigate('/instawedding'); setSidebarOpen(false); }}
                  className="w-full flex items-center gap-3 px-4 py-3 text-left text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <Camera className="w-5 h-5" />
                  Instawedding
                </button>
                <button
                  onClick={() => { navigate('/photobooth'); setSidebarOpen(false); }}
                  className="w-full flex items-center gap-3 px-4 py-3 text-left text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <Camera className="w-5 h-5" />
                  Foto Boot
                </button>
                <button
                  onClick={() => { handleLogout(); setSidebarOpen(false); }}
                  className="w-full flex items-center gap-3 px-4 py-3 text-left text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <LogOut className="w-5 h-5" />
                  Esci
                </button>
              </nav>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-6 py-4">
          {/* Header row with menu icon, title centered, empty right */}
          <div className="flex items-start justify-between">
            {/* Menu icon - Left, aligned to top */}
            <button 
              onClick={() => setSidebarOpen(true)}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors mt-1"
            >
              <Menu className="w-5 h-5" />
            </button>
            
            {/* Title, date and icon centered */}
            <div className="text-center flex-1">
              <h1 className="font-wedding text-4xl text-gray-800">{gallery?.title}</h1>
              {gallery?.event_date && (
                <p className="text-sm text-gray-500 mt-1">
                  {new Date(gallery.event_date).toLocaleDateString('it-IT', { 
                    day: 'numeric', 
                    month: 'long', 
                    year: 'numeric' 
                  })}
                </p>
              )}
              <div className="flex justify-center mt-2">
                {user?.role === 'sposi' ? (
                  <WeddingRingsIcon className="w-8 h-6" />
                ) : (
                  <Users className="w-5 h-5 text-blue-500" />
                )}
              </div>
            </div>
            
            {/* Empty space for balance */}
            <div className="w-9" />
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6">
        {/* Navigation button */}
        <div className="flex items-center justify-center gap-4 mb-6">
          <button
            onClick={() => navigate('/gallery')}
            className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg text-sm hover:bg-gray-50 transition-colors"
          >
            <Image className="w-4 h-4" />
            Tutte le foto
          </button>
        </div>

        {/* Videos Section */}
        {videos.length === 0 ? (
          <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
            <Video className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500">Nessun video disponibile</p>
          </div>
        ) : (
          <div className="space-y-8">
            {/* Video Gallery */}
            {videos.filter(v => v.video_type === 'video_gallery').map(video => (
              <div key={video.id} className="bg-white rounded-2xl overflow-hidden shadow-lg">
                <div className="bg-gradient-to-r from-blue-600 to-blue-800 px-6 py-3">
                  <h3 className="text-lg font-semibold text-white text-center">Video Gallery</h3>
                </div>
                <div className="aspect-video bg-gray-900">
                  <video 
                    src={video.url}
                    className="w-full h-full object-contain"
                    controls
                    preload="metadata"
                  />
                </div>
              </div>
            ))}
            
            {/* Video Trailer */}
            {videos.filter(v => v.video_type === 'trailer').map(video => (
              <div key={video.id} className="bg-white rounded-2xl overflow-hidden shadow-lg">
                <div className="bg-gradient-to-r from-purple-700 to-purple-900 px-6 py-3">
                  <h3 className="text-lg font-semibold text-white text-center">Video Trailer</h3>
                </div>
                <div className="aspect-video bg-gray-900">
                  <video 
                    src={video.url}
                    className="w-full h-full object-contain"
                    controls
                    preload="metadata"
                  />
                </div>
              </div>
            ))}
            
            {/* Il Video della Giornata */}
            {videos.filter(v => v.video_type === 'video_giornata').map(video => (
              <div key={video.id} className="bg-white rounded-2xl overflow-hidden shadow-lg">
                <div className="bg-gradient-to-r from-amber-600 to-amber-800 px-6 py-3">
                  <h3 className="text-lg font-semibold text-white text-center">Il Video della Giornata</h3>
                </div>
                <div className="aspect-video bg-gray-900">
                  <video 
                    src={video.url}
                    className="w-full h-full object-contain"
                    controls
                    preload="metadata"
                  />
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ClientVideosView;
